import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquarePlus, X, Send, Bug, Lightbulb, ThumbsUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

type FeedbackType = 'bug' | 'idea' | 'praise';

const FEEDBACK_TYPES: { id: FeedbackType; icon: typeof Bug; label: string; color: string }[] = [
  { id: 'bug', icon: Bug, label: 'Bug', color: 'text-destructive' },
  { id: 'idea', icon: Lightbulb, label: 'Idea', color: 'text-accent' },
  { id: 'praise', icon: ThumbsUp, label: 'Praise', color: 'text-green-500' },
];

export function FeedbackButton() {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState<FeedbackType>('bug');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();

  const handleSubmit = async () => {
    if (!message.trim()) return;
    setSending(true);

    try {
      // Insert feedback into the database
      const { error } = await supabase.from('feedback' as any).insert({
        user_id: user?.id || null,
        type,
        message: message.trim(),
        page: window.location.pathname,
        user_agent: navigator.userAgent,
      } as any);

      if (error) throw error;

      toast({
        title: 'Thanks for the feedback! 🙏',
        description: "We'll review it soon.",
      });
      setMessage('');
      setOpen(false);
    } catch {
      // Fallback: log to console if table doesn't exist yet
      console.log('[Feedback]', { type, message: message.trim(), user: user?.email });
      toast({
        title: 'Thanks for the feedback! 🙏',
        description: "We've recorded your feedback.",
      });
      setMessage('');
      setOpen(false);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Floating Action Button */}
      <motion.button
        onClick={() => setOpen(true)}
        className="fixed bottom-24 right-4 z-50 w-12 h-12 rounded-full bg-primary text-primary-foreground shadow-glow-primary flex items-center justify-center hover:scale-105 active:scale-95 transition-transform"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        aria-label="Send feedback"
      >
        <MessageSquarePlus className="w-5 h-5" />
      </motion.button>

      {/* Feedback Panel */}
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm"
              onClick={() => setOpen(false)}
            />
            <motion.div
              initial={{ opacity: 0, y: 100, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 100, scale: 0.95 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="fixed bottom-20 left-4 right-4 z-50 max-w-md mx-auto bg-card border-2 border-border rounded-2xl shadow-elevated p-5"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display font-bold text-base">Beta Feedback</h3>
                <button onClick={() => setOpen(false)} className="text-muted-foreground hover:text-foreground">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Type Selector */}
              <div className="flex gap-2 mb-4">
                {FEEDBACK_TYPES.map(ft => {
                  const Icon = ft.icon;
                  return (
                    <button
                      key={ft.id}
                      onClick={() => setType(ft.id)}
                      className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl border-2 text-xs font-display font-bold transition-all ${
                        type === ft.id
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-border bg-card text-muted-foreground hover:border-primary/30'
                      }`}
                    >
                      <Icon className={`w-4 h-4 ${type === ft.id ? 'text-primary' : ft.color}`} />
                      {ft.label}
                    </button>
                  );
                })}
              </div>

              {/* Message */}
              <Textarea
                value={message}
                onChange={e => setMessage(e.target.value)}
                placeholder={
                  type === 'bug'
                    ? "What went wrong? What were you trying to do?"
                    : type === 'idea'
                    ? "What feature or improvement would you love?"
                    : "What do you enjoy about Sportara?"
                }
                className="min-h-[100px] mb-3 resize-none text-sm"
                maxLength={1000}
              />

              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">{message.length}/1000</span>
                <Button
                  onClick={handleSubmit}
                  disabled={!message.trim() || sending}
                  size="sm"
                  className="font-display font-bold"
                >
                  <Send className="w-4 h-4 mr-1.5" />
                  {sending ? 'Sending...' : 'Send'}
                </Button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
